/**
 * dev-dimensions-toggler.js
 *
 * Custom element for the product "Dimensions & details" accordion stack.
 * Listens for variant:update on document — the variant picker lives in a different
 * .shopify-section than this element, so the event does not bubble between sections.
 *
 * Custom-bundle mode (`data-dev-bundle-dim-mode`): component tabs + sync from
 * `dev-bundle:selection-change` (configurator). Parent `variant:update` still
 * runs `#syncSlats` (parent Slats option); `#applyVariantData` is skipped.
 *
 * Lifecycle: connectedCallback inits from server HTML; after a combined-listing
 * main morph the element is replaced and connects fresh with new JSON.
 */

import { ThemeEvents } from '@theme/events';

/**
 * @typedef {{
 *   id: number,
 *   sku: string | null,
 *   dimensionsWidth: string | number | null,
 *   transparency: string[],
 *   relevantInfo: string[],
 *   finish: string[],
 *   materials: string[],
 *   dimensions: string[],
 *   packages?: PackageRow[],
 * }} VariantMetafields
 */

/**
 * @typedef {{
 *   width: number,
 *   widthUnit?: string,
 *   length: number,
 *   lengthUnit?: string,
 *   height: number,
 *   heightUnit?: string,
 *   weight: number,
 *   weightUnit?: string,
 *   count: number,
 * }} PackageRow
 */

/**
 * @typedef {{
 *   start: number,
 *   end: number,
 *   width: number,
 *   widthUnit: string,
 *   length: number,
 *   lengthUnit: string,
 *   height: number,
 *   heightUnit: string,
 *   weight: number,
 *   weightUnit: string,
 *   count: number,
 * }} PackageGroup
 */

const SECTION_ANCHOR_ID = 'product-details-section';
const TRANSPARENCY_POINT_NAMES = ['materials', 'taxes', 'transport', 'costs'];
const MAIN_ACCORDION_SELECTOR = '.dev-product-accordions__stack > details.dev-dim__outer';
const SUB_ACCORDION_SELECTOR = '.dev-dim__sub';
const BUNDLE_SELECTION_EVENT = 'dev-bundle:selection-change';
const ACCORDION_ANCHOR_EPSILON_PX = 16;
/** Opened row below this fraction of the viewport still reads as the bottom of the page. */
const ACCORDION_ANCHOR_LOW_VIEWPORT = 0.62;
/** Stacked specs column. Side-by-side desktop must not scroll the page to the opened row. */
const ACCORDION_ANCHOR_MAX_WIDTH_PX = 1019;

/** @returns {boolean} */
function accordionAnchorViewport() {
  return window.matchMedia(`(max-width: ${ACCORDION_ANCHOR_MAX_WIDTH_PX}px)`).matches;
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Sticky-header clearance for an accordion row. Prefers the row’s own `scroll-margin-top`.
 * @param {HTMLElement} summary
 * @returns {number}
 */
function accordionStickyOffset(summary) {
  const details = summary.closest('details');
  const probe = details instanceof HTMLElement ? details : summary;
  const fromMargin = Number.parseFloat(getComputedStyle(probe).scrollMarginTop);

  if (Number.isFinite(fromMargin) && fromMargin > 0) return fromMargin;

  const styles = getComputedStyle(document.body);
  const sticky = Number.parseFloat(styles.getPropertyValue('--sticky-header-offset'));
  const header = Number.parseFloat(styles.getPropertyValue('--header-height'));

  return Math.max(Number.isFinite(sticky) ? sticky : 0, Number.isFinite(header) ? header : 0) + 12;
}

/**
 * `html { scroll-behavior: smooth }` would animate a `scrollTop` write in Firefox.
 * Pin the correction so the footer flash never paints, then ease separately.
 * @param {number} top
 */
function scrollPageInstantly(top) {
  const root = document.documentElement;
  const scroller = document.scrollingElement ?? root;
  const previous = root.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  scroller.scrollTop = Math.max(0, top);
  root.style.scrollBehavior = previous;
}

/**
 * Chrome’s exclusive `<details name>` closes the previous panel and scroll-anchoring
 * then keeps whichever node it picked — often the footer, when a tall panel above collapses.
 * Drop `name` for the manual toggle so the UA doesn’t scroll on its own.
 * @param {HTMLDetailsElement[]} group
 * @returns {Map<HTMLDetailsElement, string>}
 */
function suspendDetailsName(group) {
  /** @type {Map<HTMLDetailsElement, string>} */
  const previous = new Map();

  for (const item of group) {
    const name = item.getAttribute('name');
    if (!name) continue;

    previous.set(item, name);
    item.removeAttribute('name');
  }

  return previous;
}

/** @param {Map<HTMLDetailsElement, string>} previous */
function restoreDetailsName(previous) {
  for (const [item, name] of previous) {
    item.setAttribute('name', name);
  }
}

/** @param {HTMLElement} summary @returns {number} */
function panelMotionMs(summary) {
  const details = summary.closest('details');
  const probe = details instanceof HTMLElement ? details : summary;
  const raw = getComputedStyle(probe).getPropertyValue('--dev-dim-panel-duration').trim();
  const value = Number.parseFloat(raw);

  if (!Number.isFinite(value) || value <= 0) return 0;

  return raw.endsWith('ms') ? value : value * 1000;
}

/** @param {string} str @returns {string} */
function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** @param {string} item @returns {string} */
function processListItem(item) {
  const trimmed = item.trim();
  if (!trimmed || trimmed === '/') return '';

  if (trimmed.startsWith('*')) {
    const heading = escapeHtml(trimmed.slice(1).trim());
    return `<li class="dev-dim__list-item dev-dim__list-item--header">${heading}</li>`;
  }

  if (trimmed.includes(':')) {
    const colonIndex = trimmed.indexOf(':');
    const key = escapeHtml(trimmed.slice(0, colonIndex).trim());
    const val = escapeHtml(trimmed.slice(colonIndex + 1).trim());
    return `<li class="dev-dim__list-item"><strong>${key}:</strong> ${val}</li>`;
  }

  return `<li class="dev-dim__list-item">${escapeHtml(trimmed)}</li>`;
}

/** Matches dev-dim-metafield-line.liquid mode: relevant */
/** @param {string} item @returns {string} */
function processRelevantInfoItem(item) {
  const trimmed = item.trim();
  if (!trimmed) return '';

  if (trimmed.includes(':')) {
    const firstChar = trimmed.charAt(0);
    const isUppercaseLabel = firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase();
    if (isUppercaseLabel) {
      const colonIndex = trimmed.indexOf(':');
      const key = escapeHtml(trimmed.slice(0, colonIndex).trim());
      const val = escapeHtml(trimmed.slice(colonIndex + 1).trim());
      return `<li class="dev-dim__list-item"><strong>${key}:</strong> ${val}</li>`;
    }
    return `<li class="dev-dim__list-item">${escapeHtml(trimmed)}</li>`;
  }

  return `<li class="dev-dim__list-item">${escapeHtml(trimmed)}</li>`;
}

/** @param {string} item @returns {string} */
function processDimRow(item) {
  const trimmed = item.trim();
  if (!trimmed || trimmed === '/') return '';

  if (trimmed.startsWith('*')) {
    const heading = escapeHtml(trimmed.slice(1).trim());
    return `<p class="dev-dim__dims-section-header">${heading}</p>`;
  }

  if (trimmed.includes(':')) {
    const colonIndex = trimmed.indexOf(':');
    const key = escapeHtml(trimmed.slice(0, colonIndex).trim());
    const val = escapeHtml(trimmed.slice(colonIndex + 1).trim());
    const isWidth = key === 'Width' ? ' data-name="width-id"' : '';
    const isMattressWidth = key === 'Mattress width' ? ' data-name="width-mattress-id"' : '';
    return `<div class="dev-dim__dims-row"><span class="dev-dim__dims-key">${key}:</span><span class="dev-dim__dims-val"${isWidth}${isMattressWidth}>${val}</span></div>`;
  }

  return `<p class="dev-dim__dims-note">${escapeHtml(trimmed)}</p>`;
}

/**
 * @param {Element | null} element
 * @param {string[] | undefined} data
 * @param {(item: string) => string} processor
 */
function updateList(element, data, processor) {
  if (!element) return;
  if (!Array.isArray(data) || data.length === 0) {
    element.innerHTML = '';
    return;
  }
  element.innerHTML = data.map(processor).join('');
}

/** @param {Element} picture @param {boolean} visible */
function setPictureVisibility(picture, visible) {
  picture.classList.toggle('dev-dim__picture--hidden', !visible);
  picture.setAttribute('aria-hidden', visible ? 'false' : 'true');

  for (const img of picture.querySelectorAll('img')) {
    img.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
}

/** @param {string | number | null | undefined} width */
function formatDimensionsWidth(width) {
  if (width == null || width === '') return '';
  const numeric = Number(width);
  if (Number.isFinite(numeric)) return `${Math.round(numeric)} cm`;
  return `${width} cm`;
}

/** @param {unknown} value @returns {number} */
function packageMeasureKey(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 10);
}

/** @param {unknown} value @returns {string} */
function formatPackageMeasure(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return String(Math.round(numeric));
}

/** @param {unknown} value @param {string} fallback @returns {string} */
function packageUnit(value, fallback) {
  const unit = String(value ?? '').trim().toLowerCase();
  return unit || fallback;
}

/** @param {unknown} value @param {string} unit @returns {string} */
function formatPackageValue(value, unit) {
  const amount = formatPackageMeasure(value);
  if (!amount) return '';
  return `${amount} ${unit}`;
}

/**
 * @param {PackageRow[] | undefined} rows
 * @returns {PackageGroup[]}
 */
function groupPackages(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  /** @type {PackageGroup[]} */
  const groups = [];
  /** @type {Map<string, number>} */
  const indexByKey = new Map();

  for (const row of rows) {
    const count = Math.max(1, Number(row?.count) || 1);
    const widthUnit = packageUnit(row?.widthUnit, 'cm');
    const lengthUnit = packageUnit(row?.lengthUnit, 'cm');
    const heightUnit = packageUnit(row?.heightUnit, 'cm');
    const weightUnit = packageUnit(row?.weightUnit, 'kg');
    const key = [
      packageMeasureKey(row?.width),
      widthUnit,
      packageMeasureKey(row?.length),
      lengthUnit,
      packageMeasureKey(row?.height),
      heightUnit,
      packageMeasureKey(row?.weight),
      weightUnit,
    ].join('x');
    const existingIndex = indexByKey.get(key);

    if (existingIndex == null) {
      indexByKey.set(key, groups.length);
      groups.push({
        start: 1,
        end: 1,
        width: Number(row?.width) || 0,
        widthUnit,
        length: Number(row?.length) || 0,
        lengthUnit,
        height: Number(row?.height) || 0,
        heightUnit,
        weight: Number(row?.weight) || 0,
        weightUnit,
        count,
      });
    } else {
      const existing = groups[existingIndex];
      if (existing) existing.count += count;
    }
  }

  let cursor = 1;
  for (const group of groups) {
    group.start = cursor;
    group.end = cursor + group.count - 1;
    cursor = group.end + 1;
  }

  return groups;
}

class DevDimensionsToggler extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** Cancels an in-flight scroll hold when the user toggles again or scrolls themselves. */
  #panelMotionToken = 0;

  /** @type {VariantMetafields[]} */
  #variantData = [];

  /** @type {Record<string, { width?: string, weight?: string, image?: string }>} */
  #slatsMap = {};

  /** @type {WeakMap<Element, VariantMetafields[]>} */
  #panelVariantData = new WeakMap();

  /** @type {Record<string, Record<string, { heading?: string, description?: string, image?: string, imageAlt?: string }>> | null} */
  #bundleDesignersData = null;

  /** @type {Map<number, string>} */
  #bundleHandlesByIndex = new Map();

  /** @type {number} */
  #activeBundleComponentIndex = 0;

  get #isBundleMode() {
    return this.hasAttribute('data-dev-bundle-dim-mode');
  }

  /** Same product repeated (x2/x3/x4) — one full-width tab + one dimensions panel. */
  get #isIdenticalBundleComponents() {
    return Boolean(this.querySelector('[data-dev-bundle-dim-identical]'));
  }

  /**
   * EN color for tab label composition (SSR + `retranslateLeaves`).
   * Prefer `data-swatch-color-label` over live `[data-dev-bundle-color-label]`,
   * which may already be Weglot-translated and would mix languages with EN base.
   * @param {HTMLElement} card
   * @param {string} handle
   * @returns {string}
   */
  #colorLabelFromCard(card, handle) {
    const safeHandle = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
    const swatch = handle
      ? card.querySelector(`[data-dev-bundle-swatch][data-swatch-handle="${safeHandle}"]`)
      : null;
    const fromSwatch = swatch?.getAttribute('data-swatch-color-label')?.trim() || '';
    if (fromSwatch) return fromSwatch;

    return card.querySelector('[data-dev-bundle-color-label]')?.textContent?.trim() || '';
  }

  /**
   * Dimensions panel for a configurator selection.
   * Identical xN always has a single panel (index 0); never assume index matches a skipped blank.
   * @param {number} componentIndex
   * @returns {HTMLElement | null}
   */
  #panelForSelection(componentIndex) {
    if (this.#isIdenticalBundleComponents) {
      const panel = this.querySelector('[data-dev-bundle-dim-panel]');
      return panel instanceof HTMLElement ? panel : null;
    }

    const panel = this.querySelector(
      `[data-dev-bundle-dim-panel][data-dev-bundle-component-index="${componentIndex}"]`,
    );
    return panel instanceof HTMLElement ? panel : null;
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    if (!this.#isBundleMode) {
      this.#variantData = this.#readVariantData(this);
      this.#initFromServer();
    } else {
      document.documentElement.addEventListener(BUNDLE_SELECTION_EVENT, this.#onBundleSelectionChange, {
        signal,
      });
      this.#bundleDesignersData = this.#readBundleDesignersData();
      this.#bindBundleTabs(signal);
      this.#bindBundleScrollCue(signal);
      this.#initBundleFromConfigurator();
      this.#syncBundleDesigners();
    }

    this.#bindDeepLinks(signal);
    this.#bindDrawerTriggerSync(signal);
    this.#bindExclusiveAccordions(signal);
    this.#bindSlatsInfoLink(signal);
    this.#slatsMap = this.#readSlatsMap();
    this.#syncSlats(parseInt(this.dataset.initialVariantId ?? '', 10));

    if (window.location.hash === '#slats-info') {
      const slatsDetails = this.querySelector('[data-product-accordion="slats"]');
      if (slatsDetails instanceof HTMLDetailsElement && !slatsDetails.hidden) slatsDetails.open = true;
    }
  }

  disconnectedCallback() {
    this.#panelMotionToken += 1;
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {ParentNode} root
   * @returns {VariantMetafields[]}
   */
  #readVariantData(root) {
    const el = root.querySelector(':scope > [data-dimensions-variant-data], [data-dimensions-variant-data]');
    // Prefer panel-local script (direct child) when root is a panel.
    const local =
      root instanceof Element
        ? root.querySelector(':scope > [data-dimensions-variant-data]')
        : null;
    const source = local || el;
    if (!source?.textContent) return [];

    try {
      const parsed = JSON.parse(source.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {Element} panel
   * @returns {VariantMetafields[]}
   */
  #getPanelVariantData(panel) {
    const cached = this.#panelVariantData.get(panel);
    if (cached) return cached;
    const data = this.#readVariantData(panel);
    this.#panelVariantData.set(panel, data);
    return data;
  }

  /**
   * @param {Element} panel
   * @param {string} handle
   * @returns {VariantMetafields[]}
   */
  #variantDataForHandle(panel, handle) {
    const familyEl = panel.querySelector(':scope > [data-dev-bundle-dim-family-data]');
    if (familyEl?.textContent) {
      try {
        const map = JSON.parse(familyEl.textContent);
        const list = map?.[handle];
        if (Array.isArray(list)) return list;
      } catch {
        /* fall through */
      }
    }
    return this.#getPanelVariantData(panel);
  }

  /**
   * @param {VariantMetafields[]} list
   * @param {number} variantId
   * @returns {VariantMetafields | null}
   */
  #findInList(list, variantId) {
    return list.find((v) => v.id === variantId) ?? null;
  }

  #initFromServer() {
    const variantId = parseInt(this.dataset.initialVariantId ?? '', 10);
    if (!variantId) {
      this.#openAccordionIfHashTarget();
      return;
    }

    this.#applyVariantData(variantId, this.#findInList(this.#variantData, variantId), this);
    this.#openAccordionIfHashTarget();
  }

  /**
   * @param {number} variantId
   * @param {ParentNode} root
   */
  #showVariantImages(variantId, root = this) {
    const variantPictures = root.querySelectorAll('[data-name="variant-dims-img"]');
    const sharedPictures = root.querySelectorAll('[data-name="variant-dims-shared"]');

    let hasVariantSpecific = false;

    for (const el of variantPictures) {
      const elId = parseInt(el.getAttribute('data-id') ?? '', 10);
      const isMatch = elId === variantId;
      if (isMatch) hasVariantSpecific = true;
      setPictureVisibility(el, isMatch);
    }

    for (const el of sharedPictures) {
      setPictureVisibility(el, !hasVariantSpecific);
    }
  }

  /**
   * @param {VariantMetafields | null} variantData
   * @param {ParentNode} root
   */
  #applyDimensionsWidthOverride(variantData, root = this) {
    if (!variantData) return;

    const widthOverride = formatDimensionsWidth(variantData.dimensionsWidth);
    if (!widthOverride) return;

    const widthEl = root.querySelector('[data-name="width-id"]');
    if (widthEl) widthEl.textContent = widthOverride;
  }

  /**
   * @param {VariantMetafields | null} variantData
   * @param {ParentNode} root
   */
  #syncSku(variantData, root = this) {
    const skuRow = root.querySelector('[data-name="details-item-sku-row"]');
    const skuEl = root.querySelector('[data-name="details-item-sku"]');
    const sku = (variantData?.sku ?? '').trim();

    if (skuEl) skuEl.textContent = sku;
    if (skuRow instanceof HTMLElement) skuRow.hidden = sku === '';
  }

  /**
   * @param {VariantMetafields | null} variantData
   * @param {ParentNode} root
   */
  #syncPackageDimensions(variantData, root = this) {
    const block = root.querySelector('[data-dev-pkg-dims]');
    const sub = root.querySelector('[data-dev-packaging-sub]');
    const groups = groupPackages(variantData?.packages);
    const hasPackages = groups.length > 0;

    if (block instanceof HTMLElement) {
      if (variantData) {
        block.hidden = !hasPackages;
      }

      if (hasPackages) {
        const countEl = block.querySelector('[data-dev-pkg-banner-count]');
        const grid = block.querySelector('[data-dev-pkg-grid]');
        const totalBoxes = groups.reduce((sum, group) => sum + group.count, 0);
        // Number only: the “Number of boxes:” lead is SSR (proxy-translated) and the count is `.no-translate-wg`.
        if (countEl) countEl.textContent = String(totalBoxes);

        if (grid) {
          const widthLabel = block.dataset.pkgWidth ?? '';
          const lengthLabel = block.dataset.pkgLength ?? '';
          const heightLabel = block.dataset.pkgHeight ?? '';
          const weightLabel = block.dataset.pkgWeight ?? '';
          const boxWord = block.dataset.pkgBox ?? '';

          grid.innerHTML = groups
            .map((group) => {
              // Own `<p>` for the word (Weglot segments `<p>` reliably); the number and colon are a separate `.no-translate-wg` `<p>`.
              const boxNumber = group.start === group.end ? `${group.start}` : `${group.start}-${group.end}`;

              return `<div class="dev-pkg-dims__card">
            <div class="dev-pkg-dims__box-label">
              <p class="dev-pkg-dims__box-word wg-translate">${escapeHtml(boxWord)}</p>
              <p class="dev-pkg-dims__box-number no-translate-wg">${boxNumber}:</p>
            </div>
            <div class="dev-pkg-dims__spec">
              <span class="dev-dim__dims-key wg-translate">${escapeHtml(widthLabel)}:</span>
              <span class="dev-dim__dims-val no-translate-wg">${escapeHtml(formatPackageValue(group.width, group.widthUnit))}</span>
              <span class="dev-dim__dims-key wg-translate">${escapeHtml(lengthLabel)}:</span>
              <span class="dev-dim__dims-val no-translate-wg">${escapeHtml(formatPackageValue(group.length, group.lengthUnit))}</span>
              <span class="dev-dim__dims-key wg-translate">${escapeHtml(heightLabel)}:</span>
              <span class="dev-dim__dims-val no-translate-wg">${escapeHtml(formatPackageValue(group.height, group.heightUnit))}</span>
              <span class="dev-dim__dims-key wg-translate">${escapeHtml(weightLabel)}:</span>
              <span class="dev-dim__dims-val no-translate-wg">${escapeHtml(formatPackageValue(group.weight, group.weightUnit))}</span>
            </div>
          </div>`;
            })
            .join('');
        }

        window.NoomaWeglotChrome?.retranslateLeaves?.(block);
      }
    }

    if (sub instanceof HTMLDetailsElement && variantData) {
      const hasCopy = sub.getAttribute('data-dev-packaging-has-copy') === 'true';
      const hideSub = !hasCopy && !hasPackages;
      sub.hidden = hideSub;
      if (hideSub) sub.open = false;
    }
  }

  /**
   * Append color name to bundle tab label (e.g. "Streiko Bed - Natural Oak").
   * Empty `colorLabel` is a no-op so SSR / previous text is never wiped on racey init.
   * @param {number} tabIndex - `data-dev-bundle-component-index` on the tab button
   * @param {string} [colorLabel]
   */
  #syncBundleTabLabel(tabIndex, colorLabel = '') {
    const color = (colorLabel ?? '').trim();
    if (!color) return;

    const tab = this.querySelector(
      `[data-dev-bundle-dim-tab][data-dev-bundle-component-index="${tabIndex}"]`,
    );
    if (!(tab instanceof HTMLElement)) return;

    const baseLabel = tab.getAttribute('data-dev-bundle-tab-base-label')?.trim() || '';
    if (!baseLabel) return;

    const label = `${baseLabel} - ${color}`;
    const labelHost = tab.querySelector('[data-dev-bundle-dim-tab-label]');
    if (!(labelHost instanceof HTMLElement)) return;

    for (const line of labelHost.querySelectorAll('[data-rolling-text-line]')) {
      line.textContent = label;
    }

    // Both rolling lines are `<p class="wg-translate">` — retranslate leaves under the host.
    window.NoomaWeglotChrome?.retranslateLeaves?.(labelHost);
  }

  /**
   * @param {number} variantId
   * @param {VariantMetafields | null} variantData
   * @param {ParentNode} root
   */
  #applyVariantData(variantId, variantData, root = this) {
    this.#showVariantImages(variantId, root);
    this.#syncSku(variantData, root);
    this.#syncPackageDimensions(variantData, root);

    if (!variantData) return;

    updateList(root.querySelector('[data-name="dimensions-element"]'), variantData.dimensions, processDimRow);
    updateList(root.querySelector('[data-name="materials-element"]'), variantData.materials, processListItem);
    updateList(root.querySelector('[data-name="finish-element"]'), variantData.finish, processListItem);
    updateList(
      root.querySelector('[data-name="relevant-element"]'),
      variantData.relevantInfo,
      processRelevantInfoItem,
    );

    for (const [i, name] of TRANSPARENCY_POINT_NAMES.entries()) {
      const el = root.querySelector(`[data-name="${name}-point"]`);
      if (!el) continue;
      el.textContent = variantData.transparency?.[i] ?? '';
    }

    this.#applyDimensionsWidthOverride(variantData, root);
  }

  #openAccordionIfHashTarget() {
    if (window.location.hash !== `#${SECTION_ANCHOR_ID}`) return;

    const outerDetails = this.querySelector('#dev-product-accordion-dimensions');
    if (outerDetails instanceof HTMLDetailsElement) outerDetails.open = true;
  }

  /** @param {AbortSignal} signal */
  #bindDeepLinks(signal) {
    const outerDetails = this.querySelector('#dev-product-accordion-dimensions');
    if (!(outerDetails instanceof HTMLDetailsElement)) return;

    for (const link of document.querySelectorAll(`a[href="#${SECTION_ANCHOR_ID}"]`)) {
      link.addEventListener(
        'click',
        () => {
          outerDetails.open = true;
        },
        { signal },
      );
    }
  }

  /** @returns {Record<string, { width?: string, weight?: string, image?: string }>} */
  #readSlatsMap() {
    const source = this.querySelector('[data-slats-variant-data]');
    if (!source?.textContent) return {};

    try {
      const parsed = JSON.parse(source.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /** @param {number} variantId */
  #syncSlats(variantId) {
    const details = this.querySelector('[data-product-accordion="slats"]');
    if (!(details instanceof HTMLDetailsElement)) return;

    const entry = Number.isFinite(variantId) ? this.#slatsMap[String(variantId)] : undefined;
    const width = String(entry?.width ?? '').trim();
    const weight = String(entry?.weight ?? '').trim();
    const image = String(entry?.image ?? '').trim();
    const hasData = Boolean(width || weight || image);

    details.hidden = !hasData;
    if (!hasData) details.open = false;

    for (const link of document.querySelectorAll('[data-slats-info-link]')) {
      if (!(link instanceof HTMLElement)) continue;

      link.hidden = !hasData;
      const header = link.closest('[data-variant-option-header]');
      if (header instanceof HTMLElement) {
        if (hasData) header.removeAttribute('aria-hidden');
        else header.setAttribute('aria-hidden', 'true');
      }
    }

    if (!hasData) return;

    const widthEl = details.querySelector('[data-slats-width]');
    if (widthEl) widthEl.textContent = width ? `${width} cm` : '';

    const weightEl = details.querySelector('[data-slats-weight]');
    if (weightEl) weightEl.textContent = weight ? `${weight} kg` : '';

    const imageEl = details.querySelector('[data-slats-image]');
    if (imageEl instanceof HTMLImageElement) {
      imageEl.hidden = !image;
      if (image) imageEl.src = image;
    }
  }

  /** @param {AbortSignal} signal */
  #bindSlatsInfoLink(signal) {
    const details = this.querySelector('[data-product-accordion="slats"]');
    if (!(details instanceof HTMLDetailsElement)) return;

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('[data-slats-info-link]')) return;

        event.preventDefault();
        details.open = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            details.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      },
      { signal },
    );
  }

  /** @param {AbortSignal} signal */
  #bindExclusiveAccordions(signal) {
    this.#bindExclusiveDetailsGroup(this.querySelectorAll(MAIN_ACCORDION_SELECTOR), signal);

    if (this.#isBundleMode) {
      for (const panel of this.querySelectorAll('[data-dev-bundle-dim-panel]')) {
        this.#bindExclusiveDetailsGroup(panel.querySelectorAll(SUB_ACCORDION_SELECTOR), signal);
      }
    } else {
      this.#bindExclusiveDetailsGroup(this.querySelectorAll(SUB_ACCORDION_SELECTOR), signal);
    }
  }

  /**
   * @param {NodeListOf<Element> | Element[]} detailsElements
   * @param {AbortSignal} signal
   */
  #bindExclusiveDetailsGroup(detailsElements, signal) {
    const list = [...detailsElements].filter((element) => element instanceof HTMLDetailsElement);

    for (const element of list) {
      const summary = element.querySelector('summary');

      if (summary instanceof HTMLElement) {
        summary.addEventListener(
          'click',
          (event) => {
            if (event.defaultPrevented || event.button !== 0) return;

            const target = event.target;
            if (target instanceof Element && target.closest('a, button')) return;

            event.preventDefault();
            this.#toggleAccordionFromSummary(element, list);
          },
          { signal },
        );
      }

      element.addEventListener(
        'toggle',
        () => {
          if (!element.open) return;

          for (const other of list) {
            if (other !== element && other.open) other.open = false;
          }
        },
        { signal },
      );
    }
  }

  /**
   * Open one row in the group and keep that row on screen.
   * A tall sibling closing above the tap used to leave `scrollY` pointing at the footer.
   * @param {HTMLDetailsElement} details
   * @param {HTMLDetailsElement[]} group
   */
  #toggleAccordionFromSummary(details, group) {
    const summary = details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      details.open = !details.open;
      return;
    }

    const opening = !details.open;
    const topBefore = summary.getBoundingClientRect().top;
    const openAbove =
      opening &&
      group.some((other) => other !== details && other.open && other.getBoundingClientRect().top < topBefore - 1);
    const scroller = document.scrollingElement ?? document.documentElement;
    const names = suspendDetailsName(group);

    try {
      if (opening) {
        for (const other of group) {
          if (other !== details && other.open) other.open = false;
        }
      }

      details.open = opening;
    } finally {
      restoreDetailsName(names);
    }

    // Desktop keeps the page still. Chasing the row while a panel above collapses reads as a scroll upward.
    if (!accordionAnchorViewport()) return;

    const shift = summary.getBoundingClientRect().top - topBefore;
    const restored = Math.max(0, scroller.scrollTop + shift);
    const offset = accordionStickyOffset(summary);
    const aboveHeader = topBefore < offset - ACCORDION_ANCHOR_EPSILON_PX;
    const belowFold = topBefore > window.innerHeight - summary.offsetHeight;
    const lowOnScreen = topBefore > window.innerHeight * ACCORDION_ANCHOR_LOW_VIEWPORT;
    const shouldAlign =
      accordionAnchorViewport() &&
      (opening ? aboveHeader || belowFold || (openAbove && lowOnScreen) : aboveHeader || belowFold);

    if (prefersReducedMotion() || panelMotionMs(summary) === 0) {
      if (shouldAlign) scrollPageInstantly(Math.max(0, restored + (topBefore - offset)));
      else if (Math.abs(shift) >= 1) scrollPageInstantly(restored);
      return;
    }

    if (Math.abs(shift) >= 1) scrollPageInstantly(restored);

    const pinFrom = summary.getBoundingClientRect().top;
    const pinTo = shouldAlign ? offset : pinFrom;
    this.#holdSummaryDuringPanelMotion(summary, pinFrom, pinTo);
  }

  /**
   * Panel height eases, so a one-shot scroll correction misses the collapse and the page drifts.
   * Keep the acted-on summary on its pin for the length of that transition.
   * @param {HTMLElement} summary
   * @param {number} fromTop
   * @param {number} toTop
   */
  #holdSummaryDuringPanelMotion(summary, fromTop, toTop) {
    const token = ++this.#panelMotionToken;
    const scroller = document.scrollingElement ?? document.documentElement;
    const duration = panelMotionMs(summary) + 80;
    const start = performance.now();
    const holdAbort = new AbortController();
    const release = () => {
      if (token !== this.#panelMotionToken) return;
      this.#panelMotionToken += 1;
      holdAbort.abort();
    };

    window.addEventListener('wheel', release, { passive: true, signal: holdAbort.signal });
    window.addEventListener('touchmove', release, { passive: true, signal: holdAbort.signal });

    const step = (now) => {
      if (token !== this.#panelMotionToken || !summary.isConnected) {
        holdAbort.abort();
        return;
      }

      const progress = duration <= 0 ? 1 : Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      const pin = fromTop + (toTop - fromTop) * eased;
      const delta = summary.getBoundingClientRect().top - pin;

      if (Math.abs(delta) >= 0.5) scroller.scrollTop += delta;

      if (progress < 1) {
        requestAnimationFrame(step);
        return;
      }

      holdAbort.abort();
    };

    requestAnimationFrame(step);
  }

  /** @param {AbortSignal} signal */
  #bindDrawerTriggerSync(signal) {
    const sync = () => this.#syncDrawerTriggers();

    sync();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sync, { signal, once: true });
    }
  }

  /** Unhide drawer triggers only when their target dialog-component exists on the page. */
  #syncDrawerTriggers() {
    for (const row of document.querySelectorAll('[data-drawer-target][hidden]')) {
      const drawerId = row.getAttribute('data-drawer-target');
      if (!drawerId) continue;
      // Bundle designers row visibility is owned by #syncBundleDesigners (per active component).
      if (row.hasAttribute('data-dev-bundle-designers-empty')) continue;

      row.hidden = !document.getElementById(drawerId);
    }
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    if (!this.#isEventForThisProduct(event)) return;

    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const variantId = parseInt(variant.id, 10);
    this.#syncSlats(variantId);
    if (this.#isBundleMode) return;

    this.#applyVariantData(variantId, this.#findInList(this.#variantData, variantId), this);
  };

  /** @param {CustomEvent} event @returns {boolean} */
  #isEventForThisProduct(event) {
    const eventProductId = event.detail?.data?.newProduct?.id ?? event.detail?.data?.productId;

    if (event.detail?.data?.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    }

    if (!this.dataset.productId || !eventProductId) return true;

    return this.dataset.productId === String(eventProductId);
  }

  /* ─── Custom-bundle tabs ─── */

  /** @param {AbortSignal} signal */
  #bindBundleTabs(signal) {
    const tablist = this.querySelector('[data-dev-bundle-dim-tabs]');
    if (!(tablist instanceof HTMLElement)) return;

    tablist.addEventListener(
      'click',
      (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[data-dev-bundle-dim-tab]') : null;
        if (!(tab instanceof HTMLElement) || !tablist.contains(tab)) return;
        const index = Number(tab.getAttribute('data-dev-bundle-component-index'));
        if (!Number.isFinite(index)) return;
        this.#selectBundleTab(index, { focusTab: false, scrollTab: true });
      },
      { signal },
    );

    tablist.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        const tabs = [...tablist.querySelectorAll('[data-dev-bundle-dim-tab]')];
        const current = document.activeElement;
        const currentIndex = tabs.indexOf(/** @type {Element} */ (current));
        if (currentIndex < 0) return;

        let next = -1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = (currentIndex + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        const tab = tabs[next];
        if (!(tab instanceof HTMLElement)) return;
        const index = Number(tab.getAttribute('data-dev-bundle-component-index'));
        if (!Number.isFinite(index)) return;
        this.#selectBundleTab(index, { focusTab: true, scrollTab: true });
      },
      { signal },
    );
  }

  /**
   * @param {number} componentIndex
   * @param {{ focusTab?: boolean, scrollTab?: boolean }} [opts]
   */
  #selectBundleTab(componentIndex, opts = {}) {
    const tabs = this.querySelectorAll('[data-dev-bundle-dim-tab]');
    const panels = this.querySelectorAll('[data-dev-bundle-dim-panel]');

    for (const tab of tabs) {
      if (!(tab instanceof HTMLElement)) continue;
      const idx = Number(tab.getAttribute('data-dev-bundle-component-index'));
      const selected = idx === componentIndex;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      if (selected && opts.focusTab) tab.focus();
      // Only on user tab actions — init must not scroll the PDP to Dimensions.
      // rAF: wait for active chevron width so the full label is in the scrollport.
      if (selected && opts.scrollTab) {
        requestAnimationFrame(() => {
          tab.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
        });
      }
    }

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      const idx = Number(panel.getAttribute('data-dev-bundle-component-index'));
      panel.hidden = idx !== componentIndex;
      if (idx === componentIndex) {
        const handle = panel.getAttribute('data-dev-bundle-current-handle') || '';
        if (handle) this.#bundleHandlesByIndex.set(componentIndex, handle);
      }
    }

    this.#activeBundleComponentIndex = componentIndex;
    this.#updateBundleScrollCue();
    this.#syncBundleDesigners();
  }

  /** @param {AbortSignal} signal */
  #bindBundleScrollCue(signal) {
    const scroller = this.querySelector('[data-dev-bundle-dim-tabs]');
    if (!(scroller instanceof HTMLElement)) return;

    const update = () => this.#updateBundleScrollCue();
    scroller.addEventListener('scroll', update, { signal, passive: true });
    window.addEventListener('resize', update, { signal, passive: true });

    scroller.addEventListener(
      'pointerdown',
      () => {
        const cue = this.querySelector('[data-dev-bundle-dim-scroll-cue]');
        if (cue instanceof HTMLElement) cue.dataset.dismissed = 'true';
        this.#updateBundleScrollCue();
      },
      { signal },
    );

    requestAnimationFrame(update);
  }

  #updateBundleScrollCue() {
    const scroller = this.querySelector('[data-dev-bundle-dim-tabs]');
    const cue = this.querySelector('[data-dev-bundle-dim-scroll-cue]');
    if (!(scroller instanceof HTMLElement) || !(cue instanceof HTMLElement)) return;

    // Dismiss after a light scroll so the cue does not linger over the tabs.
    if (scroller.scrollLeft > 12) {
      cue.dataset.dismissed = 'true';
    }

    if (cue.dataset.dismissed === 'true') {
      cue.hidden = true;
      return;
    }

    const overflow = scroller.scrollWidth > scroller.clientWidth + 2;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
    cue.hidden = !overflow || atEnd;
  }

  #initBundleFromConfigurator() {
    this.#openAccordionIfHashTarget();

    const root = document.querySelector('[data-dev-bundle-root]');
    const cards = root?.querySelectorAll('[data-dev-bundle-card]') ?? [];

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const index = Number(card.getAttribute('data-dev-bundle-component-index'));
      const handle = card.getAttribute('data-dev-bundle-current-handle') || '';
      const hidden = root?.querySelector(
        `[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${index}"]`,
      );
      const variantId =
        hidden instanceof HTMLInputElement ? parseInt(hidden.value, 10) : NaN;
      if (!Number.isFinite(index) || !handle || !Number.isFinite(variantId) || !variantId) continue;

      const colorLabel = this.#colorLabelFromCard(card, handle);
      this.#bundleHandlesByIndex.set(index, handle);
      this.#applyBundleSelection(index, handle, variantId, colorLabel);
      // Identical xN: one panel — seed from the first resolved card only.
      if (this.#isIdenticalBundleComponents) break;
    }

    const firstTab = this.querySelector(
      '[data-dev-bundle-dim-tab][aria-selected="true"], [data-dev-bundle-dim-tab]',
    );
    if (firstTab instanceof HTMLElement) {
      const index = Number(firstTab.getAttribute('data-dev-bundle-component-index'));
      if (Number.isFinite(index)) this.#selectBundleTab(index);
    }
  }

  /** @param {Event} event */
  #onBundleSelectionChange = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail ?? {};
    const componentIndex = Number(detail.componentIndex);
    const handle = String(detail.handle || '').trim();
    const variantId = Number(detail.variantId);
    let colorLabel = String(detail.colorLabel || '').trim();
    if (!Number.isFinite(componentIndex) || !handle || !Number.isFinite(variantId) || !variantId) return;

    // Event may omit color (or race before configurator label fill) — resolve from the card.
    if (!colorLabel) {
      const card = document.querySelector(
        `[data-dev-bundle-card][data-dev-bundle-component-index="${componentIndex}"]`,
      );
      if (card instanceof HTMLElement) {
        colorLabel = this.#colorLabelFromCard(card, handle);
      }
    }

    this.#bundleHandlesByIndex.set(componentIndex, handle);
    if (this.#isIdenticalBundleComponents) {
      this.#bundleHandlesByIndex.set(0, handle);
    }
    this.#applyBundleSelection(componentIndex, handle, variantId, colorLabel);
    if (this.#isIdenticalBundleComponents || componentIndex === this.#activeBundleComponentIndex) {
      this.#syncBundleDesigners();
    }
  };

  /**
   * @param {number} componentIndex
   * @param {string} handle
   * @param {number} variantId
   * @param {string} [colorLabel]
   */
  #applyBundleSelection(componentIndex, handle, variantId, colorLabel = '') {
    const panel = this.#panelForSelection(componentIndex);
    if (!panel) return;

    const currentHandle = panel.getAttribute('data-dev-bundle-current-handle') || '';
    if (handle && handle !== currentHandle) {
      this.#swapBundlePanelMedia(panel, handle);
      panel.setAttribute('data-dev-bundle-current-handle', handle);
      const list = this.#variantDataForHandle(panel, handle);
      this.#panelVariantData.set(panel, list);
      const script = panel.querySelector(':scope > [data-dimensions-variant-data]');
      if (script) script.textContent = JSON.stringify(list);
    }

    const list = this.#variantDataForHandle(panel, handle || currentHandle);
    this.#panelVariantData.set(panel, list);
    this.#applyVariantData(variantId, this.#findInList(list, variantId), panel);

    // Identical xN: single tab is always index 0; otherwise match the emitting card.
    const tabIndex = this.#isIdenticalBundleComponents ? 0 : componentIndex;
    this.#syncBundleTabLabel(tabIndex, colorLabel);
  }

  /**
   * Replace the panel's drawing column with a sibling product's preloaded images.
   * @param {HTMLElement} panel
   * @param {string} handle
   */
  #swapBundlePanelMedia(panel, handle) {
    const host = panel.querySelector('[data-name="dims-images-wrap"]');
    const store = this.#findSiblingImages(panel, handle);
    if (!(host instanceof HTMLElement)) return;

    // Keep a backup of the original product images under the sibling store for switch-back.
    const currentHandle = panel.getAttribute('data-dev-bundle-current-handle') || '';
    if (currentHandle) {
      let backup = this.#findSiblingImages(panel, currentHandle);
      const siblingStore = panel.querySelector('[data-dev-bundle-dim-sibling-store]');
      if (!backup && siblingStore instanceof HTMLElement) {
        backup = document.createElement('div');
        backup.setAttribute('data-dev-bundle-dim-sibling-images', '');
        backup.setAttribute('data-handle', currentHandle);
        backup.innerHTML = host.innerHTML;
        siblingStore.appendChild(backup);
      } else if (backup instanceof HTMLElement) {
        backup.innerHTML = host.innerHTML;
      }
    }

    if (store instanceof HTMLElement) {
      host.innerHTML = store.innerHTML;
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #findSiblingImages(panel, handle) {
    for (const el of panel.querySelectorAll('[data-dev-bundle-dim-sibling-images]')) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.getAttribute('data-handle') === handle) return el;
    }
    return null;
  }

  /** @returns {Record<string, Record<string, { heading?: string, description?: string, image?: string, imageAlt?: string, showCta?: boolean, rowTitle?: string, rowAria?: string }>> | null} */
  #readBundleDesignersData() {
    const el = this.querySelector('[data-dev-bundle-designers-data]');
    if (!el?.textContent) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Update Meet the designers drawer for the active Dimensions tab's current handle.
   * Also toggles the accordion row when that component has no designers content.
   */
  #syncBundleDesigners() {
    if (!this.#bundleDesignersData) return;

    const indexKey = String(this.#activeBundleComponentIndex);
    const byHandle = this.#bundleDesignersData[indexKey];
    const handle =
      this.#bundleHandlesByIndex.get(this.#activeBundleComponentIndex) ||
      this.querySelector(
        `[data-dev-bundle-dim-panel][data-dev-bundle-component-index="${this.#activeBundleComponentIndex}"]`,
      )?.getAttribute('data-dev-bundle-current-handle') ||
      '';

    const payload = handle && byHandle ? byHandle[handle] : null;
    const hasContent = !!(
      payload &&
      ((payload.heading || '').trim() ||
        (payload.description || '').trim() ||
        (payload.image || '').trim())
    );

    const trigger = document.querySelector(
      '[data-drawer-target="dev-product-designers-drawer"]',
    );
    if (trigger instanceof HTMLElement) {
      trigger.toggleAttribute('data-dev-bundle-designers-empty', !hasContent);
      trigger.hidden = !hasContent;

      const rowTitle = (payload?.rowTitle || '').trim();
      const rowAria = (payload?.rowAria || '').trim();
      const titleEl = trigger.querySelector('[data-dev-bundle-designers-row-title]');
      const labelEl = trigger.querySelector('[data-dev-bundle-designers-row-label]');
      if (titleEl instanceof HTMLElement && rowTitle) titleEl.textContent = rowTitle;
      if (labelEl instanceof HTMLElement && rowAria) labelEl.setAttribute('aria-label', rowAria);
    }

    const drawer = document.querySelector('[data-dev-bundle-designers-drawer]');
    if (!(drawer instanceof HTMLElement) || !payload) return;

    const heading = drawer.querySelector('[data-dev-bundle-designers-heading]');
    if (heading instanceof HTMLElement) {
      const text = (payload.heading || '').trim();
      heading.textContent = text;
      heading.hidden = !text;
    }

    const description = drawer.querySelector('[data-dev-bundle-designers-description]');
    if (description instanceof HTMLElement) {
      const html = payload.description || '';
      description.innerHTML = html;
      description.hidden = !html.trim();
    }

    const cta = drawer.querySelector('[data-dev-bundle-designers-cta]');
    if (cta instanceof HTMLElement) {
      cta.hidden = payload.showCta === false;
    }

    const imageWrap = drawer.querySelector('[data-dev-bundle-designers-image-wrap]');
    if (imageWrap instanceof HTMLElement) {
      const src = (payload.image || '').trim();
      const alt = (payload.imageAlt || payload.heading || '').trim();
      if (src) {
        imageWrap.innerHTML = `<img src="${src}" alt="${alt.replace(/"/g, '&quot;')}" class="dev-product-designers-drawer__image" width="1200" height="800" loading="lazy">`;
        imageWrap.hidden = false;
      } else {
        imageWrap.innerHTML = '';
        imageWrap.hidden = true;
      }
    }
  }
}

if (!customElements.get('dev-dimensions-toggler')) {
  customElements.define('dev-dimensions-toggler', DevDimensionsToggler);
}
